/* @flow */
import navigation from 'services/NavigationService';

import { apiService, userStore } from 'services/ServiceRegistry';
import { activities } from 'components/Activity/ActivityData.js';
import { Roles } from 'permissions/defs';
import { customerUsers, internalUsers } from 'permissions/userTypes';
import { companyCodeInSelectedUserProfile } from 'permissions/general';
import type { RoleId } from 'permissions/defs';

import Form from '../../forms/_.extend';

type TypeEmailNotification = {
  mail_type_id: string,
  mail_key: string,
  subscribed: boolean,
};

const userFields = (itemData: Object, t: Function, roleId: RoleId, mode: string) => {
  if (!userStore.userLanguages) {
    userStore.getUserLanguages();
  }

  const notificationSetting: Object = {};
  const setUpNotifcationsArr: Array<*> = [];
  const usersTradingAccounts: string =
    itemData && itemData.accounts && itemData.accounts.map(account => account.id).join(',');

  itemData &&
    itemData.email_notifications.map((item: TypeEmailNotification) => {
      notificationSetting[item.mail_key] = item.subscribed;

      setUpNotifcationsArr.push({
        name: item.mail_key,
        label: item.label ? item.label : t(`general:notifications.${item.mail_key}`),
        extra: {
          helperText: t(`general:notifications.${item.mail_key}_help`),
        },
        rules: '',
        value: item.subscribed,
      });

      return null;
    });

  let roleOptions = [...customerUsers, ...internalUsers].map(role => ({
    id: role,
    label: t(`general:roles.${role}`),
    value: role,
    hint: t(`general:roles.descriptions.${role}`),
  }));

  if (roleId !== Roles.SYSTEM_ADMIN) {
    roleOptions = roleOptions.filter(role => role !== Roles.SYSTEM_ADMIN);
  }

  return [
    {
      name: 'userID',
      label: t('general:users.labels.id'),
      fields: [
        {
          name: 'id',
          label: t('general:users.labels.id'),
          rules: itemData ? 'required' : '',
          value: itemData && itemData.id,
        },
      ],
    },
    {
      name: 'userProfile',
      label: t('general:users.labels.profile'),
      fields: [
        {
          name: 'firstName',
          label: t('general:labels.first_name'),
          rules: 'required|string',
          value: itemData && itemData.first_name,
        },
        {
          name: 'lastName',
          label: t('general:labels.last_name'),
          rules: 'required|string',
          value: itemData && itemData.last_name,
        },
        {
          name: 'email',
          label: t('general:labels.email_address'),
          value: itemData && itemData.email,
          rules: 'required|validEmailDomain',
        },
        {
          name: 'phoneNumber',
          label: t('general:labels.phone_number'),
          rules: 'required|telephone',
          value: itemData && itemData.tel ? itemData.tel : 'N/A',
        },
        {
          name: 'country',
          label: t('general:country'),
          rules: 'required',
          value: itemData && {
            name: itemData.country,
            id: itemData.country_id,
          },
        },
        {
          name: 'county',
          label: t('general:county'),
          rules: 'string',
          value: itemData && itemData.state,
        },
        {
          name: 'city',
          label: t('general:city'),
          rules: mode === 'edit' ? '' : 'required|string',
          value: itemData && itemData.city,
        },
        {
          name: 'postcode',
          label: t('general:postcode'),
          rules: mode === 'edit' ? '' : 'required|string',
          value: itemData && itemData.post_code ? itemData.post_code : 'N/A',
        },
        {
          name: 'state',
          label: t('general:state'),
          value: itemData && {
            name: itemData.state,
            id: itemData.state,
          },
        },
        {
          name: 'region',
          label: t('general:region'),
          rules: 'string|required_if:selectUserRole.userRole,regionUserRoleCheck',
          value: itemData && itemData.region.name,
          extra: {
            selectID: itemData && itemData.region.id,
          },
        },
        {
          name: 'company',
          rules: 'string|required',
          label: t('general:company'),
          value: itemData && itemData.company_name,
        },
      ],
    },
    {
      name: 'displaySettings',
      label: t('general:labels.display_settings'),
      fields: [
        {
          name: 'currency',
          label: t('general:currency'),
          rules: 'required',
          value: itemData && itemData.currency,
        },
        {
          name: 'price',
          label: t('general:labels.price_format'),
          rules: 'required',
          value: itemData && {
            format: itemData && itemData.price_format,
            id: itemData && itemData.price_format_id,
          },
        },
      ],
    },
    {
      name: 'notificationSettings',
      label: t('general:labels.email_notification_settings'),
      extra: {
        helperText: t('general:users.messages.disabled_notifications'),
      },
      fields: setUpNotifcationsArr,
    },
    {
      name: 'selectUserRole',
      label: t('general:actions.user_role'),
      title: 'Select User Role',
      fields: [
        {
          key: 'userRole',
          type: 'radiogroup',
          title: '',
          name: 'userRole',
          rules: 'numeric',
          label: '',
          value: itemData && typeof itemData.role_id === 'number' ? itemData.role_id : Roles.USER,
          extra: {
            roleId: itemData && itemData.role_id,
          },
          options: roleOptions,
          related: ['assignOtherAccounts.accounts', 'userProfile.region'],
        },
      ],
    },
    {
      name: 'assignOtherAccounts',
      label: itemData
        ? t('general:actions.assign_other_accounts')
        : t('general:actions.assign_accounts'),
      fields: [
        {
          name: 'accounts',
          label: t('general:actions.choose_trading_accounts_assigned_to_you'),
          type: 'textfield',
          rules: itemData ? 'string' : 'string|required_if:selectUserRole.userRole,userRoleCheck',
          value: usersTradingAccounts,
          extra: {
            shouldShowCompanyCode: companyCodeInSelectedUserProfile.includes(roleId),
            selectID: null,
            parentId:
              itemData && itemData.role_id && itemData.role_id > 5 ? itemData.region.id : '',
          },
        },
      ],
    },
    {
      name: 'accountStatus',
      label: t('general:accounts.labels.status'),
      fields: [
        {
          name: 'disabled',
          label: t('general:actions.disable_account'),
          rules: itemData ? 'required' : '',
          value: itemData && !itemData.is_enabled,
        },
      ],
    },
  ];
};

class UserProfileFormEdit extends Form {}

const userEdit = (
  fields: Array<*>,
  itemData: *,
  store: Object,
  localState: Object,
  t: Function
) => {
  const hooks: Object = {
    onSuccess: async function(form: Object) {
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;

      const formValues: Object = form.values();

      const { clicked } = formValues;

      const {
        company,
        country,
        email,
        firstName,
        lastName,
        phoneNumber,
        state,
        county,
        city,
        postcode,
        sendInvite = clicked === 'save' ? false : true,
      }: Object = formValues.userProfile;

      const languages = userStore.userLanguages || (await userStore.getUserLanguages);
      const language =
        (languages &&
          'find' in languages &&
          languages.find(_language => _language.code === 'en-US')) ||
        {};

      const { currency, price } = formValues.displaySettings;
      const { disabled } = formValues.accountStatus;
      const { accounts } = formValues.assignOtherAccounts;
      const { userRole } = formValues.selectUserRole;
      const emailNotifications: * = [];
      const stateCounty = country === 'US' ? state : county;

      itemData.email_notifications.map((item: TypeEmailNotification, idx: Number) => {
        return emailNotifications.push({
          mail_type_id: item.mail_type_id,
          mail_key: item.mail_key,
          subscribed: formValues.notificationSettings[item.mail_key],
        }); // eslint-disable-line object-shorthand
      });

      const params: Object = {
        activity_tag: activities.edited.tag, // eslint-disable-line camelcase
        first_name: firstName, // required
        last_name: lastName, // required
        tel: phoneNumber, // required
        country_id: country.id, // required
        state: stateCounty, // required
        city, // required
        postcode, // required
        company_name: company, // required
        email: email, // eslint-disable-line object-shorthand
        language: language.name, // eslint-disable-line object-shorthand
        language_id: language.id, // eslint-disable-line object-shorthand
        currency: currency.name, // eslint-disable-line object-shorthand
        currency_id: currency.id, // eslint-disable-line object-shorthand
        price_format: price.format, // eslint-disable-line object-shorthand
        price_format_id: price.id,
        email_notifications: emailNotifications, // eslint-disable-line object-shorthand
        is_enabled: !disabled,
        role_id: userRole,
        region_id: form.$('userProfile.region').extra.selectID,
        account_ids: accounts === '' ? [] : accounts.split(','),
        send_invite: sendInvite,
      };

      await apiService
        .putResource(`users/${itemData.id}`, params)
        .then((response: Object) => {
          store.updateSelectedUser(response);
          localState.isLoading = false;
          localState.responseMessage = t('general:messages.save_success');

          setTimeout(() => {
            localState.showResponseMessage = false;
            /*eslint-disable max-nested-callbacks*/
            setTimeout(() => {
              // Snackbar animation 225ms
              localState.hasSuccess = false;
              navigation.goBack();
            }, 225);
          }, 1200);

          return response;
        })
        .catch((err: *) => {
          localState.isLoading = false;
          localState.responseMessage = err.message || t('general:messages.save_error');
          localState.hasSuccess = false;
          localState.hasError = true;
        });
    },
    onError(form: Object) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };

  return new UserProfileFormEdit({ fields }, { hooks, name: t('general:actions.invite_new_user') });
};

const userInvite = (
  fields: Array<*>,
  itemData: *,
  store: Object,
  localState: Object,
  t: Function
) => {
  const hooks: Object = {
    onSuccess: async function(form: Object) {
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;

      const formValues: Object = form.values();

      const { clicked } = formValues;

      const {
        company,
        country,
        email,
        firstName,
        lastName,
        phoneNumber,
        state,
        county,
        city,
        postcode,
        sendInvite = clicked === 'save' ? false : true,
      }: Object = formValues.userProfile;
      const languages = userStore.userLanguages || (await userStore.getUserLanguages);
      const language =
        (languages &&
          'find' in languages &&
          languages.find(_language => _language.code === 'en-US')) ||
        {};
      const { currency, price }: Object = formValues.displaySettings;
      const { accounts }: Object = formValues.assignOtherAccounts;
      const { userRole }: Object = formValues.selectUserRole;
      const stateCounty = country === 'US' ? state : county;
      const params: Object = {
        activity_tag: activities.created.tag, // eslint-disable-line camelcase
        first_name: firstName, // required
        last_name: lastName, // required
        tel: phoneNumber, // required
        country_id: country.id, // required
        state: stateCounty, // required
        city, // required
        postcode, // required
        company_name: company, // required
        email: email, // eslint-disable-line object-shorthand
        language: language.name, // eslint-disable-line object-shorthand
        language_id: language.id, // eslint-disable-line object-shorthand
        currency: currency.name, // eslint-disable-line object-shorthand
        currency_id: currency.id, // eslint-disable-line object-shorthand
        price_format: price.format, // eslint-disable-line object-shorthand
        price_format_id: price.id,
        // 'email_notifications': emailNotifications, // eslint-disable-line object-shorthand
        // 'is_enabled': !disabled,
        user_role: userRole,
        region_id: form.$('userProfile.region').extra.selectID,
        account_ids: accounts === '' ? [] : accounts.split(','),
        send_invite: sendInvite,
      };

      await apiService
        .createResource('users-management', params)
        .then((response: Object) => {
          // store.updateSelectedUser(response);
          localState.isLoading = false;
          localState.responseMessage = t('general:messages.save_success');

          setTimeout(() => {
            localState.showResponseMessage = false;
            /*eslint-disable max-nested-callbacks*/
            setTimeout(() => {
              // Snackbar animation 225ms
              localState.hasSuccess = false;
              navigation.goBack();
            }, 225);
          }, 1200);

          return response;
        })
        .catch((err: *) => {
          localState.isLoading = false;
          localState.responseMessage =
            err && err.message ? err.message : t('general:messages.save_error');
          localState.hasSuccess = false;
          localState.hasError = true;
        });
    },
    onError(form: Object) {
      // console.log(form.errors());
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };

  return new UserProfileFormEdit({ fields }, { hooks, name: t('general:actions.invite_new_user') });
};

export const getForm = (
  itemData: Object,
  store: Object,
  localState: Object,
  t: Function,
  mode: string,
  userRoleId: RoleId
) => {
  const fields = userFields(itemData, t, userRoleId, mode);

  if (mode === 'edit') {
    return userEdit(fields, itemData, store, localState, t);
  } else {
    return userInvite(fields, null, store, localState, t);
  }
};
